/** Convex footprint containment for the panel packer (solarFacePacker.ts). */
import { type Vec2 } from './solarFacePacker';

// Metre-space tolerance absorbs UK lat/lng projection round-off at contact.
const EPSILON_M = 1e-7;
const dot = (a: Vec2, b: Vec2) => a.x * b.x + a.y * b.y;
const subtract = (a: Vec2, b: Vec2): Vec2 => ({ x: a.x - b.x, y: a.y - b.y });
const length = (a: Vec2) => Math.hypot(a.x, a.y);
const edges = (ring: Vec2[]): Array<[Vec2, Vec2]> =>
  ring.flatMap((a, i) => {
    const b = ring[(i + 1) % ring.length];
    return length(subtract(b, a)) > EPSILON_M ? [[a, b]] : [];
  });
const normal = (a: Vec2, b: Vec2): Vec2 => {
  const d = subtract(b, a);
  const magnitude = length(d);
  return { x: -d.y / magnitude, y: d.x / magnitude };
};

const containsPoint = (point: Vec2, boundary: Array<[Vec2, Vec2]>): boolean => {
  let inside = false;
  for (const [a, b] of boundary) {
    const ab = subtract(b, a);
    const ap = subtract(point, a);
    const t = Math.max(0, Math.min(1, dot(ap, ab) / dot(ab, ab)));
    if (Math.hypot(ap.x - ab.x * t, ap.y - ab.y * t) <= EPSILON_M) return true;
    if (
      a.y > point.y !== b.y > point.y &&
      point.x < ((b.x - a.x) * (point.y - a.y)) / (b.y - a.y) + a.x
    ) {
      inside = !inside;
    }
  }
  return inside;
};

/** Whole convex footprint containment in a shared metric frame. */
export const isConvexPolygonWithinRing = (
  panel: Vec2[],
  roof: Vec2[]
): boolean => {
  if (
    panel.length < 3 ||
    roof.length < 3 ||
    ![...panel, ...roof].every(
      (p) => Number.isFinite(p.x) && Number.isFinite(p.y)
    )
  )
    return false;
  const panelEdges = edges(panel);
  const roofEdges = edges(roof);
  const twiceArea = (boundary: Array<[Vec2, Vec2]>) =>
    Math.abs(boundary.reduce((sum, [a, b]) => sum + a.x * b.y - b.x * a.y, 0));
  if (
    twiceArea(panelEdges) <= EPSILON_M * EPSILON_M ||
    twiceArea(roofEdges) <= EPSILON_M * EPSILON_M ||
    panel.some((p) => !containsPoint(p, roofEdges))
  )
    return false;

  // All corners can touch opposite sides of a notch while the panel interior
  // is outside. A convex panel's vertex average supplies an interior sample.
  const centre = panel.reduce(
    (sum, p) => ({
      x: sum.x + p.x / panel.length,
      y: sum.y + p.y / panel.length,
    }),
    { x: 0, y: 0 }
  );
  if (!containsPoint(centre, roofEdges)) return false;

  const panelAxes = panelEdges.map(([a, b]) => normal(a, b));
  // Corners alone miss panels bridging a concave notch. A roof segment must
  // have a separating axis from the panel interior; equality is only contact.
  return roofEdges.every(([a, b]) =>
    [...panelAxes, normal(a, b)].some((axis) => {
      const values = panel.map((p) => dot(p, axis));
      const ends = [dot(a, axis), dot(b, axis)];
      return (
        Math.min(...ends) >= Math.max(...values) - EPSILON_M ||
        Math.max(...ends) <= Math.min(...values) + EPSILON_M
      );
    })
  );
};
